import type { CSSProperties } from 'react';
import { Extension } from '@tiptap/core';

import '@tiptap/extension-text-style';

import { isSupportedTextColor } from '../../Controls/constants';

import { removeEmptyTextStyle } from './helpers';
import { setMarkerOnFullySelectedItems } from './ListItem';

type ColorOptions = {
  types: string[];
};

// Read the raw style attribute rather than `element.style.color`, which reports every colour as
// `rgb(...)` and so would lose the original hex on parse.
//
// The serializer writes our rendered style string via `element.style.cssText`, and CSSOM rewrites
// colours to `rgb(...)`. So `getHTML()` — the string that gets persisted — always comes back as
// `rgb(...)`, whatever we stored. Both forms therefore have to parse, normalised to hex so that hex
// stays the single stored format the PDF Lambda reads.
const COLOR_DECLARATION_REGEX = /(?:^|;)\s*color\s*:\s*([^;]+)/i;
const HEX_COLOR_REGEX = /^#[0-9a-f]{6}$/;
// Accepts the legacy comma-separated and the CSS Color 4 space-separated forms.
const RGB_COLOR_REGEX = /^rgb\(\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*\)$/;

const toHexColor = (value: string): string | null => {
  if (HEX_COLOR_REGEX.test(value)) return value;

  const rgb = RGB_COLOR_REGEX.exec(value);
  if (!rgb) return null;
  const channels = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  if (channels.some((channel) => channel > 255)) return null;

  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

const extractStyleColor = (element: HTMLElement): string | null => {
  const style = element.getAttribute('style');
  if (!style) return null;
  const match = COLOR_DECLARATION_REGEX.exec(style);
  if (!match) return null;
  return toHexColor(match[1].trim().toLowerCase());
};

export const Color = Extension.create<ColorOptions>({
  name: 'color',

  addOptions(): ColorOptions {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          color: {
            default: null,
            parseHTML: extractStyleColor,
            renderHTML: (attributes: CSSProperties) => {
              if (!attributes.color) {
                return {};
              }
              return {
                style: `color: ${attributes.color}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setColor:
        (color: string) =>
        ({ chain, tr, state, dispatch }: any) => {
          if (!isSupportedTextColor(color)) {
            return false;
          }
          const { selection } = state;
          const applied = chain().setMark('textStyle', { color }).run();
          if (dispatch) setMarkerOnFullySelectedItems(tr, selection, 'markerColor', color);
          return applied;
        },
      unsetColor:
        () =>
        ({ chain, tr, state, dispatch }: any) => {
          const { selection } = state;
          const applied = chain().setMark('textStyle', { color: null }).run();
          if (dispatch) {
            removeEmptyTextStyle(tr, selection);
            setMarkerOnFullySelectedItems(tr, selection, 'markerColor', null);
          }
          return applied;
        },
    };
  },
});
